<template>
  <!-- 自定义图片查看器：双击 / 双指捏合 / 滚轮缩放，拖动平移，支持超长截图按宽度查看 -->
  <div v-if="value" class="img-viewer">
    <div
      ref="stage"
      class="iv-stage"
      :class="{ 'is-pannable': canPan, 'is-dragging': dragging }"
      @click="onStageClick"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
      @mousedown="onMouseDown"
      @wheel.prevent="onWheel"
    >
      <!-- 缩略图占位：原图加载完成前先显示，避免白屏 -->
      <img
        v-if="thumb && thumb !== url && !loaded && !thumbFailed"
        class="iv-img iv-img-thumb"
        :src="thumb"
        alt=""
        draggable="false"
        @error="thumbFailed = true"
      >
      <img
        ref="img"
        class="iv-img"
        :class="{ 'is-ready': loaded }"
        :src="imgSrc"
        :style="imgStyle"
        alt="图片"
        draggable="false"
        @load="onImgLoad"
        @error="onImgError"
      >
      <div v-if="!loaded" class="iv-loading">
        <template v-if="!loadFailed">
          <span class="iv-spinner"></span>
          <p>图片加载中...</p>
        </template>
        <template v-else>
          <p>图片加载失败</p>
          <button type="button" class="iv-retry" @click.stop="reload">点击重试</button>
        </template>
      </div>
    </div>

    <!-- 工具栏：仅保留 缩小 / 放大 / 关闭 三个按钮 -->
    <div class="iv-toolbar" @click.stop @mousedown.stop @touchstart.stop>
      <button type="button" class="iv-tool" @click="zoomStep(-0.5)">－</button>
      <button type="button" class="iv-tool" @click="zoomStep(0.5)">＋</button>
      <button type="button" class="iv-tool iv-tool-close" @click="close">关闭</button>
    </div>

    <p class="iv-tip">双击放大 / 缩小 · 双指捏合缩放 · 拖动查看细节</p>
  </div>
</template>

<script>
import { clampScale, fitSize, clampOffset, zoomAroundPoint, isLongImage, topAlignedOffset } from '@/utils/imageViewer'

export default {
  name: 'ImageViewer',
  props: {
    // v-model 控制显示
    value: { type: Boolean, default: false },
    // 原图地址
    url: { type: String, default: '' },
    // 缩略图地址（可选，作为加载占位）
    thumb: { type: String, default: '' },
    minScale: { type: Number, default: 0.5 },
    maxScale: { type: Number, default: 8 },
    // 双击放大到的倍数
    doubleTapScale: { type: Number, default: 2.5 },
    // 长宽比超过该值视为长图，默认按「适应宽度」展示
    longRatio: { type: Number, default: 2 }
  },
  data () {
    return {
      loaded: false,
      loadFailed: false,
      thumbFailed: false,
      reloadToken: 0,
      fitMode: 'contain', // contain=适应屏幕 width=适应宽度
      scale: 1, // 相对「适应尺寸」的缩放倍数
      offsetX: 0,
      offsetY: 0,
      animating: false,
      dragging: false,
      stageWidth: 0,
      stageHeight: 0,
      baseWidth: 0,
      baseHeight: 0,
      // 记录上一次点击时间：用于识别「双击缩放」
      lastTapAt: 0
    }
  },
  computed: {
    imgSrc () {
      if (!this.url) return ''
      if (!this.reloadToken) return this.url
      return this.url + (this.url.indexOf('?') === -1 ? '?' : '&') + '_r=' + this.reloadToken
    },
    // 图片是否超出容器（超出才可拖动）
    canPan () {
      return (this.baseWidth * this.scale > this.stageWidth + 1) ||
        (this.baseHeight * this.scale > this.stageHeight + 1)
    },
    imgStyle () {
      const transition = this.animating
        ? 'transform 0.22s ease-out, opacity 0.15s linear'
        : 'transform 0s linear, opacity 0.15s linear'
      return {
        width: this.baseWidth ? this.baseWidth + 'px' : 'auto',
        height: this.baseHeight ? this.baseHeight + 'px' : 'auto',
        transform: 'translate3d(' + this.offsetX + 'px, ' + this.offsetY + 'px, 0) scale(' + this.scale + ')',
        transition
      }
    }
  },
  watch: {
    value (visible) {
      if (visible) this.onOpen()
      else this.teardown()
    },
    url () {
      if (this.value) this.reset()
    }
  },
  mounted () {
    if (this.value) this.onOpen()
  },
  beforeDestroy () {
    this.teardown()
  },
  methods: {
    // ==================== 打开 / 关闭 ====================
    onOpen () {
      this.reset()
      this.lockScroll(true)
      window.addEventListener('resize', this.onResize)
      document.addEventListener('keydown', this.onKeydown)
      this.$nextTick(() => {
        this.measureStage()
        // 图片可能已在缓存中（load 不再触发），这里兜底初始化
        const img = this.$refs.img
        if (img && img.complete && img.naturalWidth) this.onImgLoad()
      })
    },
    teardown () {
      window.removeEventListener('resize', this.onResize)
      document.removeEventListener('keydown', this.onKeydown)
      this.unbindDrag()
      if (this._animTimer) {
        clearTimeout(this._animTimer)
        this._animTimer = null
      }
      this.lockScroll(false)
    },
    close () {
      this.$emit('input', false)
      this.$emit('close')
    },
    // 打开时禁止背景页面滚动（移动端体验）
    lockScroll (lock) {
      if (!document.body) return
      if (lock) {
        this._prevBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = this._prevBodyOverflow || ''
        document.documentElement.style.overflow = ''
      }
    },
    onKeydown (e) {
      if (e.key === 'Escape' || e.keyCode === 27) this.close()
    },
    // 窗口尺寸变化（如手机横竖屏切换）后重新适配，保留当前缩放倍数
    onResize () {
      const scale = this.scale
      this.measureStage()
      this.applyBaseSize()
      this.scale = scale
      this.settle()
    },
    // 复位状态（每次打开 / 切换图片时调用）
    reset () {
      this.loaded = false
      this.loadFailed = false
      this.thumbFailed = false
      this.animating = false
      this.dragging = false
      this.fitMode = 'contain'
      this.scale = 1
      this.offsetX = 0
      this.offsetY = 0
      this.baseWidth = 0
      this.baseHeight = 0
      this.lastTapAt = 0
    },
    // 加载失败后重试
    reload () {
      this.loadFailed = false
      this.loaded = false
      this.reloadToken += 1
    },
    // ==================== 尺寸计算 ====================
    measureStage () {
      const stage = this.$refs.stage
      if (!stage) return
      const rect = stage.getBoundingClientRect()
      this.stageWidth = rect.width
      this.stageHeight = rect.height
    },
    applyBaseSize () {
      const img = this.$refs.img
      if (!img || !img.naturalWidth) return
      const size = fitSize({
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        stageWidth: this.stageWidth,
        stageHeight: this.stageHeight,
        mode: this.fitMode
      })
      this.baseWidth = size.width
      this.baseHeight = size.height
      this._fitScale = size.fitScale
    },
    onImgLoad () {
      const img = this.$refs.img
      if (!img) return
      this.loaded = true
      this.loadFailed = false
      this.measureStage()
      // 长图（超长截图）默认「适应宽度」：否则按屏幕缩放后文字会小到看不清
      if (isLongImage({
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        ratio: this.longRatio
      })) {
        this.fitMode = 'width'
      }
      this.applyBaseSize()
      this.resetTransform()
    },
    onImgError () {
      this.loaded = false
      this.loadFailed = true
    },
    // 边界收敛：保证图片不会被拖出可视区域
    settle () {
      const next = clampOffset({
        x: this.offsetX,
        y: this.offsetY,
        baseWidth: this.baseWidth,
        baseHeight: this.baseHeight,
        scale: this.scale,
        stageWidth: this.stageWidth,
        stageHeight: this.stageHeight
      })
      this.offsetX = next.x
      this.offsetY = next.y
    },
    // 程序化动画开关（拖动时不能用 transition，否则跟手会飘）
    animate (duration = 240) {
      this.animating = true
      if (this._animTimer) clearTimeout(this._animTimer)
      this._animTimer = setTimeout(() => {
        this.animating = false
        this._animTimer = null
      }, duration)
    },
    // ==================== 缩放 ====================
    // 复位缩放：回到当前适应模式的原始比例；长图从顶部开始看
    resetTransform () {
      this.scale = 1
      this.offsetX = 0
      this.offsetY = topAlignedOffset({
        baseHeight: this.baseHeight,
        scale: 1,
        stageHeight: this.stageHeight
      })
      this.animate()
    },
    // 以 clientX / clientY 为锚点缩放到指定倍数（不传坐标则以屏幕中心为锚点）
    zoomTo (targetScale, clientX, clientY, animate) {
      const next = clampScale(targetScale, this.minScale, this.maxScale)
      if (!this.baseWidth) {
        this.scale = next
        return
      }
      const stage = this.$refs.stage ? this.$refs.stage.getBoundingClientRect() : null
      let anchorX = 0
      let anchorY = 0
      if (stage) {
        const centerX = stage.left + stage.width / 2
        const centerY = stage.top + stage.height / 2
        anchorX = (typeof clientX === 'number' ? clientX : centerX) - centerX
        anchorY = (typeof clientY === 'number' ? clientY : centerY) - centerY
      }
      const zoomed = zoomAroundPoint({
        scale: this.scale,
        x: this.offsetX,
        y: this.offsetY,
        targetScale: next,
        anchorX,
        anchorY
      })
      this.scale = zoomed.scale
      this.offsetX = zoomed.x
      this.offsetY = zoomed.y
      this.settle()
      if (animate !== false) this.animate()
    },
    zoomStep (delta) {
      this.zoomTo(this.scale + delta)
    },
    // 双击：未放大时放大到 doubleTapScale，已放大则复位
    toggleZoom (clientX, clientY) {
      if (this.scale > 1.05) {
        this.resetTransform()
      } else {
        this.zoomTo(this.doubleTapScale, clientX, clientY)
      }
    },
    // 切换适应模式（适应宽度 / 适应屏幕）
    // 说明：预览界面只保留「－ / ＋ / 关闭」三个按钮，此方法供外部按需调用
    // （例如父组件通过 this.$refs.viewer.setFitMode('contain') 切回整图）
    setFitMode (mode) {
      if (this.fitMode === mode) return
      this.fitMode = mode
      this.applyBaseSize()
      this.resetTransform()
    },
    // 1:1 原始大小（按原图像素显示）
    // 说明：预览界面只保留「－ / ＋ / 关闭」三个按钮，此方法供外部按需调用
    setActualSize () {
      if (!this._fitScale) return
      this.zoomTo(1 / this._fitScale)
    },
    // ==================== 手势 / 鼠标 ====================
    isImageTarget (target) {
      return !!(target && target.classList && target.classList.contains('iv-img'))
    },
    onStageClick (e) {
      // 拖动 / 双指操作后浏览器仍会派发 click，这里忽略掉
      if (this._moved || this._suppressClick) {
        this._moved = false
        this._suppressClick = false
        return
      }
      // 点击空白处关闭
      if (!this.isImageTarget(e.target)) {
        this.close()
        return
      }
      // 双击（两次点击间隔 < 300ms）放大 / 复位
      const now = Date.now()
      if (now - this.lastTapAt < 300) {
        this.lastTapAt = 0
        this.toggleZoom(e.clientX, e.clientY)
        return
      }
      this.lastTapAt = now
    },
    onTouchStart (e) {
      this._moved = false
      this._suppressClick = false
      if (e.touches.length === 1) {
        this._pinching = false
        this._startX = e.touches[0].clientX
        this._startY = e.touches[0].clientY
        this._startOffsetX = this.offsetX
        this._startOffsetY = this.offsetY
      } else if (e.touches.length >= 2) {
        // 双指捏合：记录起始距离 / 中点 / 偏移，移动时按比例缩放并跟随中点
        this._pinching = true
        this._moved = true
        this._suppressClick = true
        this._pinchStartDistance = this.touchDistance(e.touches)
        this._pinchStartScale = this.scale
        this._pinchStartOffsetX = this.offsetX
        this._pinchStartOffsetY = this.offsetY
        const mid = this.touchMidpoint(e.touches)
        this._pinchStartMid = mid
        const stage = this.$refs.stage ? this.$refs.stage.getBoundingClientRect() : null
        this._pinchAnchorX = stage ? mid.x - (stage.left + stage.width / 2) : 0
        this._pinchAnchorY = stage ? mid.y - (stage.top + stage.height / 2) : 0
      }
    },
    onTouchMove (e) {
      if (e.touches.length === 1 && !this._pinching) {
        const dx = e.touches[0].clientX - this._startX
        const dy = e.touches[0].clientY - this._startY
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
          this._moved = true
          this._suppressClick = true
        }
        if (!this._moved) return
        this.dragging = true
        const next = clampOffset({
          x: this._startOffsetX + dx,
          y: this._startOffsetY + dy,
          baseWidth: this.baseWidth,
          baseHeight: this.baseHeight,
          scale: this.scale,
          stageWidth: this.stageWidth,
          stageHeight: this.stageHeight
        })
        this.offsetX = next.x
        this.offsetY = next.y
      } else if (e.touches.length >= 2 && this._pinching) {
        const distance = this.touchDistance(e.touches)
        const ratio = (this._pinchStartDistance && distance) ? distance / this._pinchStartDistance : 1
        const targetScale = clampScale(this._pinchStartScale * ratio, this.minScale, this.maxScale)
        const realRatio = this._pinchStartScale ? targetScale / this._pinchStartScale : 1
        const mid = this.touchMidpoint(e.touches)
        const startMid = this._pinchStartMid || mid
        this.scale = targetScale
        // 保持捏合起始中点在屏幕上的位置不变，并跟随中点移动
        this.offsetX = this._pinchStartOffsetX -
          (this._pinchAnchorX - this._pinchStartOffsetX) * (realRatio - 1) + (mid.x - startMid.x)
        this.offsetY = this._pinchStartOffsetY -
          (this._pinchAnchorY - this._pinchStartOffsetY) * (realRatio - 1) + (mid.y - startMid.y)
        this.settle()
      }
    },
    onTouchEnd (e) {
      if (!e.touches || e.touches.length === 0) {
        this._pinching = false
        this.dragging = false
      }
    },
    touchDistance (touches) {
      if (!touches || touches.length < 2) return 0
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    },
    touchMidpoint (touches) {
      if (!touches || touches.length < 2) return { x: 0, y: 0 }
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      }
    },
    // 鼠标拖动（PC 浏览器 / 开发者工具）
    onMouseDown (e) {
      if (e.button !== 0) return
      this._moved = false
      this._suppressClick = false
      this.dragging = false
      this._dragging = true
      this._startX = e.clientX
      this._startY = e.clientY
      this._startOffsetX = this.offsetX
      this._startOffsetY = this.offsetY
      document.addEventListener('mousemove', this.onMouseMove)
      document.addEventListener('mouseup', this.onMouseUp)
    },
    onMouseMove (e) {
      if (!this._dragging) return
      const dx = e.clientX - this._startX
      const dy = e.clientY - this._startY
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this._moved = true
        this._suppressClick = true
        this.dragging = true
      }
      if (!this._moved) return
      const next = clampOffset({
        x: this._startOffsetX + dx,
        y: this._startOffsetY + dy,
        baseWidth: this.baseWidth,
        baseHeight: this.baseHeight,
        scale: this.scale,
        stageWidth: this.stageWidth,
        stageHeight: this.stageHeight
      })
      this.offsetX = next.x
      this.offsetY = next.y
    },
    onMouseUp () {
      this.unbindDrag()
    },
    unbindDrag () {
      document.removeEventListener('mousemove', this.onMouseMove)
      document.removeEventListener('mouseup', this.onMouseUp)
      this._dragging = false
      this.dragging = false
    },
    // 滚轮缩放（PC 端）：以光标位置为锚点，跟手不跳
    onWheel (e) {
      const factor = e.deltaY > 0 ? 0.85 : 1.18
      this.zoomTo(this.scale * factor, e.clientX, e.clientY, false)
    }
  }
}
</script>

<style lang="less" scoped>
.img-viewer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5000;
  background: rgba(0, 0, 0, 0.92);
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;

  .iv-stage {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    touch-action: none; /* 手势交给 JS 处理，屏蔽浏览器默认缩放 / 滚动 */
    cursor: default;

    &.is-pannable { cursor: grab; }
    &.is-dragging { cursor: grabbing; }
  }

  /* 图片：宽高与位移全部由 JS 计算（保证缩放 / 平移边界精确） */
  .iv-img {
    display: block;
    max-width: none;
    max-height: none;
    transform-origin: center center;
    will-change: transform;
    backface-visibility: hidden;
    -webkit-user-drag: none;
    opacity: 0;

    &.is-ready { opacity: 1; }
  }

  /* 缩略图占位：原图加载中先显示，避免白屏 */
  .iv-img-thumb {
    position: absolute;
    top: 50%;
    left: 50%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    opacity: 0.55;
    filter: blur(1px);
    transform: translate(-50%, -50%);
  }

  .iv-loading {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    text-align: center;
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;

    p { margin-top: 10px; }
  }

  .iv-spinner {
    display: inline-block;
    width: 26px;
    height: 26px;
    border: 2px solid rgba(255, 255, 255, 0.25);
    border-top-color: #ff9211;
    border-radius: 50%;
    animation: iv-rotate 0.8s linear infinite;
  }

  .iv-retry {
    margin-top: 12px;
    padding: 6px 18px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 16px;
    background: transparent;
    color: #fff;
    font-size: 13px;
  }

  /* 底部操作栏：仅 缩小 / 放大 / 关闭 三个橙色按钮 */
  .iv-toolbar {
    position: fixed;
    left: 50%;
    bottom: calc(48px + env(safe-area-inset-bottom));
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.35);
    transform: translateX(-50%);
  }

  .iv-tool {
    flex-shrink: 0;
    min-width: 52px;
    height: 36px;
    padding: 0 14px;
    border: none;
    border-radius: 18px;
    background: #ff9211; /* 橙色按钮 */
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    line-height: 36px;
    text-align: center;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(255, 146, 17, 0.35);

    &:active { background: #e57f00; }
  }

  .iv-tool-close {
    min-width: 68px;
    font-size: 14px;
  }

  .iv-tip {
    position: fixed;
    left: 0;
    right: 0;
    bottom: calc(16px + env(safe-area-inset-bottom));
    z-index: 2;
    text-align: center;
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
  }
}

@keyframes iv-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
